<template>
  <v-container class="filters-container pl-6 py-0">
    <v-row class="pt-2">
      <h3>Select Report Type</h3>
    </v-row>
    <v-row>
      <v-select
        :menu-props="{ maxHeight: '400' }"
        v-model="reportType"
        label="Report Type"
        :items="reportTypes"
        @change="handleChangeReportType"
      ></v-select>
    </v-row>

    <div v-if="showCountrySelector">
      <v-row class="pt-2">
        <h3>Select Country</h3>
      </v-row>
      <v-row>
        <v-col cols="12" class="countrySelectCol pa-0">
          <v-autocomplete
            v-model="countryFilters"
            label="Countries"
            :items="countries"
            item-text="name"
            item-value="iso2"
            return-object
            clearable
            single-line
            dense
          />
        </v-col>
      </v-row>
    </div>

    <div v-if="showRiskRatingTypeSelector">
      <v-row>
        <v-col cols="12" class="riskRatingTypeSelector pa-0">
          <v-switch
            v-model="city"
            class="event-input-toggle pa-0 ma-0"
            solo
            label="Add Cities To Report"
            return-object
          ></v-switch>
        </v-col>
        <v-col cols="12" class="riskRatingTypeSelector pa-0">
          <v-switch
            v-model="zone"
            class="event-input-toggle pa-0 ma-0"
            solo
            label="Add Zones To Report"
            return-object
          ></v-switch>
        </v-col>
      </v-row>
    </div>

    <div v-if="showCountryReportSubsectionSelector">
      <SelectAllDropdownSelector
        v-model="countryReportSubsectionSelection"
        :item-list="countryReportSubsectionsDisplay"
        title="Select Required Sections"
      >
      </SelectAllDropdownSelector>
      <div class="pa-4">
        *Please note Scenarios and Election Watch are not available for all
        countries.
      </div>
    </div>

    <div v-if="showCitySelector">
      <v-row class="pt-2">
        <h3>Select City</h3>
      </v-row>
      <v-row>
        <v-col cols="12" class="citySelectCol pa-0">
          <v-autocomplete
            v-model="cityFilters"
            label="Cities"
            :items="cities"
            item-text="name"
            item-value="code"
            return-object
            clearable
            single-line
            dense
          />
        </v-col>
      </v-row>
    </div>

    <div v-if="showCityReportSubsectionSelector">
      <SelectAllDropdownSelector
        v-model="cityReportSubsectionSelection"
        :item-list="cityReportSubsectionsDisplay"
        title="Select Required Sections"
      >
      </SelectAllDropdownSelector>
      <SelectAllDropdownSelector
        v-if="cityReportSubsectionSelection.includes('embassies')"
        v-model="embassyCountriesSelection"
        :item-list="embassyCountries"
        :defaultAllSelected="true"
        title="Country Embassy Selection"
      >
      </SelectAllDropdownSelector>
      <SelectAllDropdownSelector
        v-model="countryReportSubsectionSelection"
        :item-list="cityReportCountryReportSectionsDisplay"
        title="Country Report Subsections"
      >
      </SelectAllDropdownSelector>
    </div>

    <div v-if="showWaterSelector">
      <v-row class="pt-2">
        <h3>Select Water</h3>
      </v-row>
      <v-row>
        <v-col cols="12" class="waterSelectCol pa-0">
          <v-autocomplete
            v-model="waterFilters"
            label="Waters"
            :items="waters"
            item-text="name"
            item-value="code"
            return-object
            clearable
            single-line
            dense
          />
        </v-col>
      </v-row>
    </div>

    <div v-if="showWaterReportSubsectionSelector">
      <SelectAllDropdownSelector
        v-model="waterReportSubsectionSelection"
        :item-list="waterReportSubsectionsDisplay"
        title="Select Required Sections"
      >
      </SelectAllDropdownSelector>
    </div>

    <v-row dense>
      <v-btn
        block
        color="primary"
        medium
        @click="handleRunReport"
        :disabled="runReportBtnIsDisabled || loading"
      >
        Run Report
        <Loader
          v-if="loading"
          size="small"
          color="black"
          text=""
          class="pl-2"
        ></Loader>
      </v-btn>
    </v-row>
  </v-container>
</template>

<script>
import lodashOrderBy from "lodash/orderBy";

import {
  getControlRiskCountries,
  getCities,
  getWaters,
} from "@/common/maps/utilities";
import { isFeatureUnlocked } from "@/common/featureLock.js";
import {
  getCountryReport,
  getCustomCountryReport,
  getCityReport,
  getCustomCityReport,
  getWaterReport,
  getCustomWaterReport,
  getRiskRatingReport,
} from "@/services/reporting/reporting.service";
import {
  hasEssentialsAccess,
  hasPlusCyberAccess,
  hasCitySummaryAccess,
  hasMaritimeAccess,
  hasGrdAccess,
} from "@/services/user/user.service";
import { pageTrackerLogger } from "@/common/pageTracker";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";
import SelectAllDropdownSelector from "@/components/common/SelectAllDropdownSelector.vue";
import { getEmbassyCountriesConfig } from "@/services/common/embassy.service";

const DEFAULT_VALUES = {
  countryFilters: [],
  cityFilters: [],
  waterFilters: [],
};

const REPORT_TYPES = {
  countryReport: "Country Report",
  customCountryReport: "Custom Country Report",
  cityReport: "City Report",
  customCityReport: "Custom City Report",
  waterReport: "Water Report",
  customWaterReport: "Custom Water Report",
  riskRatingReport: "Risk Rating Report",
};

//tech debt: swap this to ids and text fields
const COUNTRY_REPORT_SUBSECTIONS_SEERIST_ESSENTIALS = {
  riskRatings: "Risk Ratings",
  overview: "Country Overview",
  electionWatch: "Election Watch",
  political: "Political",
  operational: "Operational",
  security: "Security",
  profile: "Profile",
  scenarios: "Scenarios",
  analysisUpdates: "Analysis Updates",
  cityReports: "City Reports",
};

const COUNTRY_REPORT_SUBSECTION_FULL_SEERIST = {
  pulse: "Pulse",
};

const COUNTRY_REPORT_SUBSECTION_CYBER_SEERIST = {
  cyber: "Cyber",
};

const COUNTRY_REPORT_SUBSECTION_MARITIME = {
  maritime: "Maritime",
  ports: "Ports",
};

const COUNTRY_REPORT_SUBSECTION_ALL_ORDERED = {
  riskRatings: "Risk Ratings",
  pulse: "Pulse",
  overview: "Overview",
  analysisUpdates: "Analysis Updates",
  electionWatch: "Election Watch",
  political: "Political",
  operational: "Operational",
  security: "Security",
  maritime: "Maritime",
  cyber: "Cyber",
  profile: "Profile",
  scenarios: "Scenarios",
  ports: "Ports",
  cityReports: "City Reports",
};

const CITY_REPORT_SUBSECTION_ALL_ORDERED = isFeatureUnlocked(
  "city-page-enhancements"
)
  ? {
      riskRatings: "Risk Ratings",
      overview: "Overview",
      recommendations: "Travel Recommendations",
      embassies: "Embassy and Consulates",
      keyIssues: "Key Issues",
      facts: "Facts",
      arrivals: "Arrivals",
    }
  : {
      riskRatings: "Risk Ratings",
      overview: "Overview",
      keyIssues: "Key Issues",
      facts: "Facts",
      arrivals: "Arrivals",
    };

const WATER_REPORT_SUBSECTION_ALL_ORDERED = {
  riskRatings: "Risk Ratings",
  overview: "Overview",
  analysisUpdates: "Analysis Updates",
};

export default {
  name: "ReportBuilder",
  components: {
    SelectAllDropdownSelector,
    Loader: () => import("@/components/common/Loader"),
  },
  data() {
    const hasCityAccess = hasCitySummaryAccess();
    if (!hasCityAccess) {
      delete REPORT_TYPES.cityReport;
      delete REPORT_TYPES.customCityReport;
    }
    const hasMaritime = hasMaritimeAccess();
    if (!hasMaritime) {
      delete REPORT_TYPES.waterReport;
      delete REPORT_TYPES.customWaterReport;
    }
    const hasGrd = hasGrdAccess();
    if (hasGrd) {
      delete REPORT_TYPES.customCountryReport;
    }

    return {
      reportType: [],
      loading: false,
      city: false,
      zone: false,
      countryFilters: DEFAULT_VALUES.countryFilters,
      countryReportSubsectionSelection: [],
      countryName: null,
      countryList: [],
      embassyCountries: [],
      embassyCountriesSelection: [],
      cityFilters: DEFAULT_VALUES.cityFilters,
      cityReportSubsectionSelection: [],
      cityName: null,
      cityList: [],
      waterFilters: DEFAULT_VALUES.waterFilters,
      waterReportSubsectionSelection: [],
      waterName: null,
      waterList: [],
    };
  },
  mounted() {
    this.initialize();
  },
  computed: {
    countries() {
      return this.countryList;
    },
    cities() {
      return lodashOrderBy(this.cityList, "name", "asc");
    },
    waters() {
      return lodashOrderBy(this.waterList, "name", "asc");
    },
    reportTypes() {
      return this.getDisplayStrings(REPORT_TYPES);
    },
    countryReportSubsectionsDisplay() {
      let subsections = COUNTRY_REPORT_SUBSECTIONS_SEERIST_ESSENTIALS;
      if (!hasEssentialsAccess()) {
        subsections = {
          ...subsections,
          ...COUNTRY_REPORT_SUBSECTION_FULL_SEERIST,
        };
      }
      if (hasPlusCyberAccess()) {
        subsections = {
          ...subsections,
          ...COUNTRY_REPORT_SUBSECTION_CYBER_SEERIST,
        };
      }
      if (hasMaritimeAccess()) {
        subsections = {
          ...subsections,
          ...COUNTRY_REPORT_SUBSECTION_MARITIME,
        };
      }
      const items = [];
      for (const [key, value] of Object.entries(
        COUNTRY_REPORT_SUBSECTION_ALL_ORDERED
      )) {
        if (subsections.hasOwnProperty(key)) {
          items.push({
            text: value,
            value: key,
          });
        }
      }
      return items;
    },
    showCountrySelector() {
      return (
        this.reportType === REPORT_TYPES.countryReport ||
        this.reportType === REPORT_TYPES.customCountryReport
      );
    },
    showRiskRatingTypeSelector() {
      return this.reportType === REPORT_TYPES.riskRatingReport;
    },
    showCountryReportSubsectionSelector() {
      return this.reportType === REPORT_TYPES.customCountryReport;
    },
    cityReportSubsectionsDisplay() {
      return Object.entries(CITY_REPORT_SUBSECTION_ALL_ORDERED).map(
        ([k, v]) => {
          return { text: v, value: k };
        }
      );
    },
    cityReportCountryReportSectionsDisplay() {
      return this.countryReportSubsectionsDisplay.filter(
        (s) => s.value !== "cityReports"
      );
    },
    showCitySelector() {
      return (
        this.reportType === REPORT_TYPES.cityReport ||
        this.reportType === REPORT_TYPES.customCityReport
      );
    },
    showCityReportSubsectionSelector() {
      return this.reportType === REPORT_TYPES.customCityReport;
    },
    waterReportSubsectionsDisplay() {
      return Object.entries(WATER_REPORT_SUBSECTION_ALL_ORDERED).map(
        ([k, v]) => {
          return { text: v, value: k };
        }
      );
    },
    showWaterSelector() {
      return (
        this.reportType === REPORT_TYPES.waterReport ||
        this.reportType === REPORT_TYPES.customWaterReport
      );
    },
    showWaterReportSubsectionSelector() {
      return this.reportType === REPORT_TYPES.customWaterReport;
    },
    runReportBtnIsDisabled() {
      switch (this.reportType) {
        case REPORT_TYPES.countryReport:
          if (this.countryFilters != null && this.countryFilters.iso2) {
            return false;
          }
          break;
        case REPORT_TYPES.customCountryReport:
          if (
            this.countryFilters != null &&
            this.countryFilters.iso2 &&
            this.countryReportSubsectionSelection.length != 0
          ) {
            return false;
          }
          break;
        case REPORT_TYPES.cityReport:
          if (this.cityFilters != null && this.cityFilters.code) {
            return false;
          }
          break;
        case REPORT_TYPES.customCityReport:
          if (
            this.cityFilters != null &&
            this.cityFilters.code &&
            this.cityReportSubsectionSelection.length != 0
          ) {
            return false;
          }
          break;
        case REPORT_TYPES.waterReport:
          if (this.waterFilters != null && this.waterFilters.code) {
            return false;
          }
          break;
        case REPORT_TYPES.customWaterReport:
          if (
            this.waterFilters != null &&
            this.waterFilters.code &&
            this.waterReportSubsectionSelection.length != 0
          ) {
            return false;
          }
          break;
        case REPORT_TYPES.riskRatingReport:
          return false;
          break;
        default:
          return true;
      }
      return true;
    },
  },
  methods: {
    async initialize() {
      let countries = await getControlRiskCountries();
      this.embassyCountries = await getEmbassyCountriesConfig();
      this.countryList = countries || [];
      let cities = (await getCities()) || [];
      this.cityList = cities
        .filter((city) => city.inRiskRatings)
        .map((city) => {
          return {
            code: city.code,
            name: `${city.name[0].text}, ${city.country.name}`,
          };
        });
      let waters = (await getWaters()) || [];
      this.waterList = waters.map((water) => {
        return {
          code: water.code,
          name: water.name[0].text,
        };
      });
    },
    async handleRunReport() {
      let matomoName = this.reportType;
      this.loading = true;
      let requestTimestamp = new Date().toISOString();
      switch (this.reportType) {
        case REPORT_TYPES.countryReport:
          matomoName += " - " + this.countryFilters.name;
          await getCountryReport(
            this.getCountryReportParams(),
            requestTimestamp
          );
          break;
        case REPORT_TYPES.customCountryReport:
          await getCustomCountryReport(
            this.getCustomCountryReportParams(),
            requestTimestamp
          );
          matomoName += " - " + this.countryFilters.name;
          break;
        case REPORT_TYPES.cityReport:
          matomoName += " - " + this.cityFilters.name;
          await getCityReport(this.getCityReportParams(), requestTimestamp);
          break;
        case REPORT_TYPES.customCityReport:
          await getCustomCityReport(
            this.getCustomCityReportParams(),
            requestTimestamp
          );
          matomoName += " - " + this.cityFilters.name;
          break;
        case REPORT_TYPES.waterReport:
          matomoName += " - " + this.waterFilters.name;
          await getWaterReport(this.getWaterReportParams(), requestTimestamp);
          break;
        case REPORT_TYPES.customWaterReport:
          await getCustomWaterReport(
            this.getCustomWaterReportParams(),
            requestTimestamp
          );
          matomoName += " - " + this.waterFilters.name;
          break;
        case REPORT_TYPES.riskRatingReport:
          await getRiskRatingReport(
            this.getRiskRatingReportParams(),
            requestTimestamp
          );
          break;
      }

      this.loading = false;
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.reportWidget,
        EVENT_ACTIONS.clicked,
        matomoName
      );
    },
    handlePageTrackerEvent(category, action, name) {
      let _paq = (window._paq = window._paq || []);
      _paq.push(["trackEvent", category, action, name]);
      pageTrackerLogger(
        "Page Tracker Event " +
          category +
          " " +
          action +
          " " +
          name +
          " logged."
      );
    },
    getCountryReportParams() {
      return {
        countryCode: this.countryFilters.iso2.toUpperCase(),
      };
    },
    getCustomCountryReportParams() {
      return {
        countryCode: this.countryFilters.iso2.toUpperCase(),
        // api expects camelCase for subsection names
        options: this.formatSections(this.countryReportSubsectionSelection),
      };
    },
    getCityReportParams() {
      return {
        cityCode: this.cityFilters.code.toUpperCase(),
      };
    },
    getCustomCityReportParams() {
      return {
        cityCode: this.cityFilters.code.toUpperCase(),
        // api expects camelCase for subsection names
        options: this.formatSections(this.cityReportSubsectionSelection),
        countrySections: this.formatSections(
          this.countryReportSubsectionSelection
        ),
        embassyCountries: this.embassyCountriesSelection,
      };
    },
    getWaterReportParams() {
      return {
        waterCode: this.waterFilters.code.toUpperCase(),
      };
    },
    getCustomWaterReportParams() {
      return {
        waterCode: this.waterFilters.code.toUpperCase(),
        // api expects camelCase for subsection names
        options: this.formatSections(this.waterReportSubsectionSelection),
      };
    },
    getRiskRatingReportParams() {
      // if zone toggle is active, it should include zones in report.
      // if city toggle is active, it should include cities in report.
      // if city and zone are not included it should just download country report.
      return this.zone && this.city
        ? { include: ["zone", "city"] }
        : this.zone
        ? { include: ["zone"] }
        : this.city
        ? { include: ["city"] }
        : { include: null };
    },
    getDisplayStrings(obj) {
      let displayStrings = [];
      for (const [key, value] of Object.entries(obj)) {
        displayStrings.push(value);
      }
      return displayStrings;
    },
    handleChangeReportType() {
      this.countryFilters = [];
      this.countryReportSubsectionSelection = [];
      this.cityFilters = [];
      this.cityReportSubsectionSelection = [];
      this.waterFilters = [];
      this.waterReportSubsectionSelection = [];
      this.city = false;
      this.zone = false;
    },
    formatSections(sections) {
      return sections.map((subsection) => {
        subsection = subsection.replace(/\s/g, "");
        return subsection.charAt(0).toLowerCase() + subsection.slice(1);
      });
    },
  },
};
</script>

<style scoped>
.filters-container {
  max-height: calc(100vh - 80px);
  overflow-y: auto;
  overflow-x: hidden;
}
.riskRatingTypeSelector .v-label {
  font-size: 13px;
}
</style>
